import React from "react";

const RELOAD_KEY = "dynamic-import-retry";

/**
 * Vite fires `vite:preloadError` on `window` when a dynamically imported
 * chunk fails to load. We capture that flag so `isChunkError` works
 * regardless of per-browser error message wording.
 */
const chunkErrors = new WeakSet<Error>();

window.addEventListener("vite:preloadError", (e: Event) => {
	const payload = (e as CustomEvent).detail?.error;
	if (payload instanceof Error) {
		chunkErrors.add(payload);
	}
});

export function isChunkError(error: unknown): boolean {
	if (!(error instanceof Error)) return false;
	if (chunkErrors.has(error)) return true;
	// Fallback: direct dynamic-import failures that bypass Vite's preload helper
	const msg = error.message.toLowerCase();
	return (
		msg.includes("dynamically imported module") ||
		msg.includes("importing a module script failed") ||
		error.name === "ChunkLoadError"
	);
}

/**
 * Wrapper around React.lazy that auto-reloads the page once when a
 * dynamic import fails (e.g. after a deployment changed chunk hashes).
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
	importFn: () => Promise<{ default: T }>,
) {
	return React.lazy(() =>
		importFn().catch((error) => {
			if (!isChunkError(error)) throw error;

			const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
			if (!hasReloaded) {
				sessionStorage.setItem(RELOAD_KEY, "1");
				window.location.reload();
				// Return a never-resolving promise so React doesn't render the error
				return new Promise(() => {});
			}

			// Already reloaded once — clear flag and let the error boundary handle it
			sessionStorage.removeItem(RELOAD_KEY);
			throw error;
		}),
	);
}
